type Orientation = 'horizontal' | 'vertical'
import { Favorite, MoreVertIcon } from '@tunasong/icons'
import { capitalize, dayjs, isFacade, isPersistedEntity, isScaffold, SystemTags } from '@tunasong/models'
import type { RenderMenuItemProps } from '@tunasong/plugin-lib'
import { isEntity, isPersisted } from '@tunasong/schemas'
import cn from 'classnames'
import type { FC, MouseEvent, ReactNode } from 'react'
import React, { useState } from 'react'
import { useDeviceInfo } from '../browser/device.hook.js'
import { useUIExtensions } from '../extensions.hook.js'
import { useMergeRefs } from '../hooks/merge-refs.js'
import { TunaEntityBreadcrumbs } from '../navigation/entity-breadcrumbs.js'
import { UserAvatar } from '../profile/index.js'
import { TagList } from '../tags/tag-list.js'
import { useCurrentUser } from '@tunasong/redux'
import { SketchBadge } from './sketch-badge.js'
import { entityTypeLabel } from './sort-filter-util.js'
import { EntityIcon } from './entity-icon.js'
import { metaSeparator } from './meta-separator.js'
import { EntityListItemFacade } from './entity-list-item-facade.js'
import { StyledEntityAvatar, StyledListItemAvatar, StyledListItemText } from './list-item.styles.js'
import { usePreloadOnHover } from './preload-on-hover.js'
import {
  IconButton,
  ListItemIcon,
  ListItem,
  type ListItemProps,
  ListItemButton,
  Menu,
  MenuTrigger,
} from '../primitives/index.js'
import styles from './entity-list-item.module.css'

/** Pick only the ListItem-specific props to avoid HTMLAttributes<HTMLLIElement> vs HTMLAttributes<HTMLDivElement> conflicts with RenderMenuItemProps */
type ListItemOwnProps = Pick<
  ListItemProps,
  'dense' | 'button' | 'selected' | 'disabled' | 'disablePadding' | 'disableGutters' | 'secondaryAction' | 'alignItems'
>

export interface EntityListItemProps extends RenderMenuItemProps, ListItemOwnProps {
  /** Mark the item as a favorite */
  favorite?: boolean
  size?: 'tiny' | 'small' | 'large' | 'medium'
  hideSubtitle?: boolean
  orientation?: Orientation
  iconStyle?: 'avatar' | 'icon'
  showBreadcrumbs?: boolean | 'inactiveLinks'
  /** Phase 2: gate the last-editor avatar/dot. Defaults to true. */
  showLastEditor?: boolean
  /** Phase 2 a11y: optional prefix for the row's accessible name (e.g. "Pinned: "). */
  itemAriaLabelPrefix?: string
  /**
   * `columns`: one dense line with the name, a type column, a right-aligned time and a named overflow menu, in
   * place of the stacked "Song. Updated: ..." subtitle. An untitled entity reads "Untitled <type>".
   * `compact`: the stacked row, with a "Song · 12 days ago" meta line in place of "Song. Updated: ...".
   * @default 'default'
   */
  layout?: 'default' | 'columns' | 'compact'
  /** `columns` layout only: show the type column, e.g. when the list is not filtered to one type. @default true */
  showType?: boolean
  /**
   * Keep the overflow button on touch screens, where rows otherwise leave the actions to a long press: sized as a
   * 44px target and named "More actions for <name>". The `columns` layout always does this. @default false
   */
  alwaysShowActions?: boolean
  /**
   * `columns` layout only: a line under the name, e.g. Home's "Band · 14 songs · 3 setlists". Mirrors
   * `EntityCard`'s `meta`. On phones the time joins this line and the type column is dropped, so the row stays
   * two lines.
   */
  meta?: ReactNode
  /** `columns` layout only: presentational content before the time column, e.g. a member avatar stack */
  trailing?: ReactNode
  /** `columns` layout only: the ISO time the time column shows in place of `updatedAt`, e.g. a space's last activity */
  activityAt?: string
  ref?: React.Ref<HTMLLIElement>
  onClick?(ev: MouseEvent): void
}

export const EntityListItem: FC<EntityListItemProps> = props => {
  const {
    className,
    favorite,
    orientation = 'horizontal',
    element,
    size,
    hideSubtitle = false,
    iconStyle = 'icon',
    actions = true,
    showBreadcrumbs = false,
    showLastEditor = true,
    itemAriaLabelPrefix,
    style,
    layout = 'default',
    showType = true,
    alwaysShowActions = false,
    meta,
    trailing,
    activityAt,
    onClick,
    ref,
    ...restProps
  } = props
  const isColumns = layout === 'columns'

  const me = useCurrentUser()

  const { isMobile } = useDeviceInfo()

  // If we have a plugin, we can show the item enabled

  const disabled = props.disabled // || !hasPlugin

  const [localRef, setLocalRef] = useState<HTMLLIElement | null>(null)
  const mergedRef = useMergeRefs(setLocalRef, ref)

  usePreloadOnHover({ entityId: element?.id, containerEl: localRef })

  const uiExtensions = useUIExtensions()

  const label = element.name
  // Phase 2 dashboard pivot: surface the *last editor* not the owner — but only when
  // somebody ELSE edited last. A self-edit indicator is noise (every row in your own
  // recent items would carry one); showing the avatar only for others' edits makes
  // its presence meaningful. Falls back to owner when `lastEditedBy` is absent
  // (legacy entities / search docs indexed before the field landed).
  const lastEditorId = isPersisted(element) ? (element.lastEditedBy ?? element.userId) : undefined
  const isSelfEdit = lastEditorId === me.userId
  const showLastEditorAvatar = showLastEditor && lastEditorId !== undefined && !isSelfEdit
  // Strip the marker tags from the chip list — the SketchBadge carries the signal instead.
  // 'sys:ai-generated' (canonical), 'sys:scaffold' (status) and the legacy 'tunabrain' tag are
  // all filtered to cover historical data.
  const visibleTags = (element.tags ?? []).filter(
    t => t !== SystemTags.AIGenerated && t !== SystemTags.Scaffold && t !== 'tunabrain'
  )
  /**
   * Status, not provenance: a kept image is the user's and carries no badge in any list.
   * Labelling work the musician decided to keep is what the research says devalues it.
   */
  const showSketchBadge = isPersistedEntity(element) && isScaffold(element)
  const subtitle =
    isPersistedEntity(element) && !hideSubtitle && !isColumns ? (
      <>
        {layout === 'compact'
          ? `${entityTypeLabel(element.type)}${metaSeparator}${dayjs(element.updatedAt).fromNow()}`
          : `${capitalize(element.type)}. Updated: ${dayjs(element.updatedAt).fromNow()}`}
        {element.description ? ' - ' + element.description : ''}
        {showSketchBadge ? <SketchBadge size="sm" /> : null}
        <TagList style={{ display: 'block', marginLeft: 8 }} tags={visibleTags} size="small" />
      </>
    ) : null

  /**
   * Actions: canonical Menu composition (GH #1496). The MoreVert button is the
   * MenuTrigger; the context-menu gesture (right-click, Shift+F10, long-press)
   * opens the same menu anchored at the row. The ItemMenu is expensive, so it
   * mounts on first open and then stays mounted so its dialogs outlive the menu.
   */
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [menuMounted, setMenuMounted] = React.useState(false)
  const [contextAnchorEl, setContextAnchorEl] = React.useState<Element | null>(null)
  const handleMenuOpenChange = (open: boolean) => {
    setMenuOpen(open)
    if (open) {
      setMenuMounted(true)
      return
    }
    // The next open via the trigger anchors to the trigger again.
    setContextAnchorEl(null)
  }
  const handleActionsClose = () => handleMenuOpenChange(false)
  // The row is itself clickable; keep a trigger click from opening the entity.
  const stopPropagation = (ev: React.MouseEvent) => ev.stopPropagation()

  const title = disabled ? `No plugin for ${element.type}` : undefined

  /** The columns layout, or a list that opts in, keeps its overflow button on touch screens too, as a 44px target */
  const isTouchSafeAction = isColumns || alwaysShowActions
  const showMenuAction = (isTouchSafeAction || !isMobile) && isPersisted(element)

  const handleContext = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    setContextAnchorEl(ev.currentTarget)
    handleMenuOpenChange(true)
  }
  const ItemMenu = actions && !disabled ? uiExtensions.entityList?.ItemMenu : undefined

  if (!(isEntity(element) && isPersisted(element))) {
    return null
  }

  const rightPadding = isMobile ? 0 : showLastEditorAvatar ? 12 : 8
  /** The open target is the row's focus stop, so it carries the row's name when a prefix is set. */
  const ariaLabel = itemAriaLabelPrefix ? `${itemAriaLabelPrefix}${element.name ?? ''}` : undefined
  const typeLabel = entityTypeLabel(element.type)
  const hasMeta = meta !== undefined && meta !== null
  const timeLabel = dayjs(activityAt ?? element.updatedAt).fromNow()
  const displayName = element.name || `Untitled ${typeLabel.toLowerCase()}`
  const defaultActionsLabel = element.name ? `Actions for ${element.name}` : 'Actions'
  const actionsLabel = isTouchSafeAction ? `More actions for ${displayName}` : defaultActionsLabel
  const actionsButtonClass = isTouchSafeAction ? styles.touchAction : undefined

  return isFacade(element) ? (
    <EntityListItemFacade
      className={className}
      element={element}
      style={style}
      {...restProps}
      ref={mergedRef as React.Ref<HTMLDivElement>}
      /** Override disabled if we have no plugin here */
      disabled={disabled}
    />
  ) : (
    <ListItem
      {...(restProps as Omit<typeof restProps, keyof React.DOMAttributes<HTMLDivElement>>)}
      ref={mergedRef as React.Ref<HTMLLIElement>}
      className={className}
      disabled={disabled}
      /* Merged, not replaced: DraggableListItem passes its drag and drop-target feedback in through `style` */
      style={{
        ...style,
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      title={title}
      data-orientation={orientation}
      data-entity-id={element.id}
      /** Bubbles from the open target, so the ContextMenu key and Shift+F10 open the actions menu. */
      onContextMenu={handleContext as React.MouseEventHandler<HTMLLIElement>}
      secondaryAction={
        <div className={styles.secondaryAction}>
          {favorite ? (
            <span title="You have this as a favorite">
              <Favorite color="var(--tuna-palette-error-main)" />
            </span>
          ) : null}
          {actions && !disabled ? (
            <>
              {showLastEditorAvatar && lastEditorId ? (
                <UserAvatar userId={lastEditorId} size="tiny" lastEditedContext />
              ) : null}
              {ItemMenu ? (
                <Menu open={menuOpen} onOpenChange={handleMenuOpenChange}>
                  {showMenuAction ? (
                    <MenuTrigger
                      render={
                        <IconButton
                          color="inherit"
                          className={actionsButtonClass}
                          aria-label={actionsLabel}
                          onClick={stopPropagation}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      }
                    />
                  ) : null}
                  {/* Expensive component so we mount it on demand */}
                  {menuMounted ? (
                    <ItemMenu entity={element} anchorEl={contextAnchorEl} onClose={handleActionsClose} />
                  ) : null}
                </Menu>
              ) : showMenuAction ? (
                <IconButton color="inherit" className={actionsButtonClass} aria-label={actionsLabel}>
                  <MoreVertIcon />
                </IconButton>
              ) : null}
            </>
          ) : null}
        </div>
      }
    >
      <ListItemButton
        disableRipple={true}
        className={styles.listItemButton}
        aria-label={ariaLabel}
        onClick={disabled ? undefined : (onClick as React.MouseEventHandler<HTMLDivElement>)}
      >
        {iconStyle === 'avatar' ? (
          <StyledListItemAvatar size={size}>
            <StyledEntityAvatar type={element.type} size={size} />
          </StyledListItemAvatar>
        ) : null}
        {iconStyle === 'icon' ? (
          <ListItemIcon
            style={{
              justifyContent: orientation === 'vertical' ? 'center' : undefined,
            }}
          >
            <EntityIcon entity={element} />
          </ListItemIcon>
        ) : null}

        {isColumns ? (
          <div className={cn(styles.columns, hasMeta && styles.withSubline)}>
            <span className={styles.columnsMain}>
              {element.name ? (
                <span className={styles.columnsName}>{element.name}</span>
              ) : (
                <span className={`${styles.columnsName} ${styles.untitled}`} data-untitled>
                  {displayName}
                </span>
              )}
              {hasMeta ? (
                <span className={styles.columnsSubline} data-testid="row-meta">
                  {meta}
                  <span className={styles.sublineTime}>
                    {metaSeparator}
                    {timeLabel}
                  </span>
                </span>
              ) : null}
            </span>
            {trailing ? <span className={styles.columnsTrailing}>{trailing}</span> : null}
            <span className={styles.columnsMeta}>
              {showType ? <span className={styles.columnsType}>{typeLabel}</span> : null}
              <span className={styles.columnsTime}>{timeLabel}</span>
            </span>
          </div>
        ) : (
          <StyledListItemText
            style={{
              paddingRight: orientation === 'vertical' ? 0 : rightPadding,
            }}
            primary={
              <>
                {label}
                {showBreadcrumbs ? (
                  <TunaEntityBreadcrumbs
                    entity={element}
                    showHome={false}
                    activeLinks={showBreadcrumbs === 'inactiveLinks' ? false : showBreadcrumbs}
                  />
                ) : null}
              </>
            }
            secondary={subtitle}
          />
        )}
      </ListItemButton>
    </ListItem>
  )
}
